import { useState, useCallback } from "react";
import {
  ApiDataResponse,
  ApiResponse,
  Subject,
  BehavioralSubject,
} from "../../services/Dtos";
import { ApiService } from "../../services/ApiService";
import useAxios from "../useAxios";
import { useSchoolsContext } from "../../Contexts/schoolContext";
import { useApiResponseHandler } from "../useApiResponseHandler";

export const useSubjectService = () => {
  const { setSubjects, setBehavioralSubjects } = useSchoolsContext();
  const axiosClient = useAxios();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const { handleApiError, handleApiSuccess, clearErrors } =
    useApiResponseHandler({
      setErrors,
    });

  // Subject operations
  const createSubject = useCallback(
    async (
      subject: Subject,
      onSuccess?: (subject: Subject) => void
    ): Promise<{ result: ApiDataResponse<Subject> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = (await new ApiService<Subject>(
          "/subjects",
          axiosClient
        ).create(subject)) as ApiDataResponse<Subject>;

        if (result.succeeded && result.data) {
          const createdSubject = result.data;
          setSubjects((prev: Subject[]) =>
            [...prev, createdSubject].sort((a, b) =>
              a.subjectName.localeCompare(b.subjectName)
            )
          );
          handleApiSuccess("Subject created successfully");
          onSuccess?.(createdSubject);
          return { result };
        } else {
          handleApiError(result, "Failed to create subject");
          return { result: { ...result, data: null as any } };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to create subject");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to create subject",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: null as any,
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess, setSubjects]
  );

  const updateSubject = useCallback(
    async (
      id: string,
      subject: Subject,
      onSuccess?: (subject: Subject) => void
    ): Promise<{ result: ApiDataResponse<Subject> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = (await new ApiService<Subject>(
          "/subjects",
          axiosClient
        ).update(id, subject)) as ApiDataResponse<Subject>;

        if (result.succeeded) {
          const updatedSubject = subject;
          setSubjects((prev: Subject[]) =>
            prev.map((s) => (s.id === id ? updatedSubject : s))
          );
          handleApiSuccess("Subject updated successfully");
          onSuccess?.(updatedSubject);
          return { result };
        } else {
          handleApiError(result, "Failed to update subject");
          return { result: { ...result, data: null as any } };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to update subject");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to update subject",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: null as any,
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess, setSubjects]
  );

  const deleteSubject = useCallback(
    async (
      id: string,
      onSuccess?: (id: string) => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<Subject>(
          "/subjects",
          axiosClient
        ).delete(id);
        if (result.succeeded) {
          setSubjects((prev: Subject[]) => prev.filter((s) => s.id !== id));
          handleApiSuccess("Subject deleted successfully");
          onSuccess?.(id);
          return { result };
        } else {
          handleApiError(result, "Failed to delete subject");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to delete subject");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to delete subject",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
        setOpenDeleteDialog(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess, setSubjects]
  );

  // Behavioral Subject operations
  const createBehavioralSubject = useCallback(
    async (
      behavioralSubject: BehavioralSubject,
      onSuccess?: (behavioralSubject: BehavioralSubject) => void
    ): Promise<{ result: ApiDataResponse<BehavioralSubject> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = (await new ApiService<BehavioralSubject>(
          "/behavioralSubjects",
          axiosClient
        ).create(behavioralSubject)) as ApiDataResponse<BehavioralSubject>;

        if (result.succeeded && result.data) {
          const createdBehavioralSubject = result.data;
          setBehavioralSubjects((prev: BehavioralSubject[]) =>
            [...prev, createdBehavioralSubject].sort((a, b) =>
              a.name.localeCompare(b.name)
            )
          );
          handleApiSuccess("Behavioral subject created successfully");
          onSuccess?.(createdBehavioralSubject);
          return { result };
        } else {
          handleApiError(result, "Failed to create behavioral subject");
          return { result: { ...result, data: null as any } };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(
          error,
          "Failed to create behavioral subject"
        );
        return {
          result: {
            succeeded: false,
            message:
              errorMessage?.message || "Failed to create behavioral subject",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: null as any,
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess, setBehavioralSubjects]
  );

  const updateBehavioralSubject = useCallback(
    async (
      id: string,
      behavioralSubject: BehavioralSubject,
      onSuccess?: (behavioralSubject: BehavioralSubject) => void
    ): Promise<{ result: ApiDataResponse<BehavioralSubject> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = (await new ApiService<BehavioralSubject>(
          "/behavioralSubjects",
          axiosClient
        ).update(id, behavioralSubject)) as ApiDataResponse<BehavioralSubject>;

        if (result.succeeded) {
          const updatedBehavioralSubject = behavioralSubject;
          setBehavioralSubjects((prev: BehavioralSubject[]) =>
            prev.map((s) => (s.id === id ? updatedBehavioralSubject : s))
          );
          handleApiSuccess("Behavioral subject updated successfully");
          onSuccess?.(updatedBehavioralSubject);
          return { result };
        } else {
          handleApiError(result, "Failed to update behavioral subject");
          return { result: { ...result, data: null as any } };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(
          error,
          "Failed to update behavioral subject"
        );
        return {
          result: {
            succeeded: false,
            message:
              errorMessage?.message || "Failed to update behavioral subject",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: null as any,
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess, setBehavioralSubjects]
  );

  const deleteBehavioralSubject = useCallback(
    async (
      id: string,
      onSuccess?: (id: string) => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<BehavioralSubject>(
          "/behavioralSubjects",
          axiosClient
        ).delete(id);
        if (result.succeeded) {
          setBehavioralSubjects((prev: BehavioralSubject[]) =>
            prev.filter((s) => s.id !== id)
          );
          handleApiSuccess("Behavioral subject deleted successfully");
          onSuccess?.(id);
          return { result };
        } else {
          handleApiError(result, "Failed to delete behavioral subject");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(
          error,
          "Failed to delete behavioral subject"
        );
        return {
          result: {
            succeeded: false,
            message:
              errorMessage?.message || "Failed to delete behavioral subject",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
        setOpenDeleteDialog(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess, setBehavioralSubjects]
  );

  return {
    // Subject operations
    createSubject,
    updateSubject,
    deleteSubject,
    // Behavioral Subject operations
    createBehavioralSubject,
    updateBehavioralSubject,
    deleteBehavioralSubject,
    // Shared state
    isLoading,
    errors,
    clearErrors,
    openDeleteDialog,
    setOpenDeleteDialog,
  };
};
